import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
} from "@fluentui/react-components";
import { Tag20Regular } from "@fluentui/react-icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FieldTypes } from "../../models/FieldTypes";
import { FlatVsaaTag } from "../../models/VsaaTag";
import { stopKeyDownPropagation } from "../../services/ReactGridService";
import { DisplayField } from "../DisplayField";
import { TreeTagPicker } from "./TreeTagPicker";

const useStyles = makeStyles({
  dialogSurface: {
    minWidth: "800px",
  },
  tagsBtn: {
    paddingTop: "6px",
  },
  tagsBtnDisabled: {
    paddingTop: "6px",
    opacity: "0.6",
    cursor: "not-allowed",
  },
  tagsBtnEnabled: {
    paddingTop: "6px",
    opacity: "1",
    cursor: "pointer",
  },
  tagsButtonWithIcon: {
    minWidth: "fit-content",
  },
});

interface TreeTagPickerDialogProps {
  tagCollectionId: string;
  label: string;
  multiValue?: boolean;
  values: FlatVsaaTag[];
  allTags: FlatVsaaTag[];
  showTagsAsDialogTrigger?: boolean;
  onSubmit: (selectedTags: FlatVsaaTag[]) => void;
  rearrange?: boolean;
  copy?: boolean;
  onClose?: () => void;
  usedTagsId?: string[];
  usedTagLabel?: string;
  processWithSubActivitiesId?: string[];
  error?: string | null;
  disabled?: boolean;
  disableRootNodes?: boolean;
}

export const TreeTagPickerDialog = (props: TreeTagPickerDialogProps) => {
  const classes = useStyles();
  const [editedTags, setEditedTags] = useState<FlatVsaaTag[] | undefined>();
  const [open, setOpen] = useState<boolean>(
    () => document.activeElement?.className === "rg-hidden-element"
  );

  const { t } = useTranslation();
  const {
    label,
    multiValue,
    values,
    allTags,
    onSubmit,
    showTagsAsDialogTrigger,
    onClose,
    usedTagsId,
    usedTagLabel,
    processWithSubActivitiesId,
    error,
    disabled,
    disableRootNodes,
  } = props;

  const selectedTags = editedTags ?? values;

  const onClickSave = () => {
    onSubmit(selectedTags);
    setEditedTags(undefined);
    setOpen(false);
  };

  const handleChange = (tagOrTags: FlatVsaaTag | FlatVsaaTag[]) => {
    if (Array.isArray(tagOrTags)) {
      setEditedTags(tagOrTags);
    } else if (tagOrTags) {
      setEditedTags([tagOrTags]);
    } else {
      setEditedTags([]);
    }
  };

  const onClickClose = () => {
    if (onClose) {
      onClose();
    }

    setEditedTags(undefined);
    setOpen(false);
  };

  return (
    <Dialog open={open}>
      <DialogTrigger disableButtonEnhancement>
        {showTagsAsDialogTrigger ? (
          <div
            role="button"
            className={
              disabled || error
                ? classes.tagsBtnDisabled
                : classes.tagsBtnEnabled
            }
            onCopy={(e) => e.stopPropagation()}
            onCut={(e) => e.stopPropagation()}
            onPaste={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => !disabled && !error && setOpen(true)}
            onKeyDown={stopKeyDownPropagation}
          >
            <DisplayField
              type={FieldTypes.Tag}
              label={label}
              value={values}
              disableTextWrap
            />
          </div>
        ) : (
          <Button
            className={classes.tagsButtonWithIcon}
            onCopy={(e) => e.stopPropagation()}
            onCut={(e) => e.stopPropagation()}
            onPaste={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => !disabled && !error && setOpen(true)}
            onKeyDown={stopKeyDownPropagation}
            disabled={disabled || !!error}
          >
            {props.copy ? (
              t("copyTag")
            ) : props.rearrange ? (
              t("moveTag")
            ) : (
              <Tag20Regular />
            )}
          </Button>
        )}
      </DialogTrigger>
      <DialogSurface
        onCopy={(e) => e.stopPropagation()}
        onCut={(e) => e.stopPropagation()}
        onPaste={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onKeyDown={stopKeyDownPropagation}
        className={classes.dialogSurface}
        aria-describedby={undefined}
      >
        <DialogBody>
          <DialogTitle>
            {t("select")} {label}
          </DialogTitle>
          <DialogContent>
            <TreeTagPicker
              tags={allTags}
              values={selectedTags}
              multiValue={multiValue}
              onChange={handleChange}
              rearrange={props?.rearrange || props?.copy}
              usedTagsId={usedTagsId}
              usedTagLabel={usedTagLabel}
              processWithSubActivitiesId={processWithSubActivitiesId}
              disableRootNodes={disableRootNodes}
            />
          </DialogContent>
          <DialogActions>
            <DialogTrigger disableButtonEnhancement>
              <Button onClick={onClickClose} appearance="secondary">
                {t("close")}
              </Button>
            </DialogTrigger>
            <DialogTrigger disableButtonEnhancement>
              <Button
                type="submit"
                onClick={onClickSave}
                appearance="primary"
                disabled={
                  props.rearrange || props.copy
                    ? selectedTags.length === 0
                    : false
                }
              >
                {props.copy
                  ? t("copy")
                  : props.rearrange
                    ? t("move")
                    : t("save")}
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
